import Calendar16 from '@carbon/icons-react/es/calendar/16';
import { Space } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus, WarningErrorCode, WarningType } from 'api/account';
import { PermissionSetType } from 'api/models';
import { Alert } from 'components/atoms/alert';
import { Caption01, Text02 } from 'components/styles';
import { ROCKETLANE_DEMO_MEETING } from 'constant';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useAccount } from 'hooks/use-account';

const AnnouncementBanner = styled(Alert)`
  padding: var(--spacing-03) var(--spacing-06);
  .ant-alert-message {
    width: auto;
    text-align: center;
    ${Caption01};
  }
`;

const Container = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: var(--zindex-modal);
  display: flex;
  justify-content: center;

  ${AnnouncementBanner} {
    width: 100%;
  } ;
`;

const BuyLink = styled.span`
  margin-left: var(--spacing-02);
  text-decoration: underline;
  cursor: pointer;
`;

const Main = styled.div``;

const DemoLink = styled.a`
  display: flex;
  align-items: center;
  ${Text02};
  > svg {
    margin-right: var(--spacing-02);
  }
`;

export const TrialExpiryBanner = ({ onBuy }: { onBuy: () => void }) => {
  const { expiryDate, status } = useAccount();
  const currentUserPermissionSetType = useUserPermissionSetType();

  const { expiringIn, isExpiringSoon, isAlreadyExpired } = useMemo(() => {
    const today = moment().endOf('day');
    const expiry = moment(expiryDate).endOf('day');
    const result = today.diff(expiry, 'days');
    const _isAlreadyExpired = result > 0;

    return {
      expiringIn: _isAlreadyExpired ? 0 : Math.abs(result),
      isAlreadyExpired: _isAlreadyExpired,
      isExpiringSoon: Math.abs(result) <= 7,
    };
  }, [expiryDate]);

  if (status !== AccountStatus.Trial) {
    return null;
  }

  return (
    <Container id="account-expiry-banner">
      <AnnouncementBanner
        type={isExpiringSoon ? 'warning' : 'info'}
        closable={false}
        message={
          <Space size={96}>
            <Main>
              {isAlreadyExpired ? (
                <FormattedMessage id="account.trial.expired.message" />
              ) : (
                <FormattedMessage
                  id="account.trial.expiry.message"
                  values={{ days: expiringIn }}
                />
              )}

              {!(
                isAlreadyExpired &&
                currentUserPermissionSetType === PermissionSetType.AccountAdmin
              ) ? (
                <BuyLink onClick={onBuy}>
                  <FormattedMessage id="account.trial.buy" />
                </BuyLink>
              ) : null}
            </Main>
            <DemoLink href={ROCKETLANE_DEMO_MEETING} target="_blank">
              <Calendar16 />
              <FormattedMessage id="account.trial.book_demo" />
            </DemoLink>
          </Space>
        }
      />
    </Container>
  );
};

export const SubscriptionRenewalFailedBanner = () => {
  const { accountWarnings } = useAccount();
  const hasWarning = useMemo(
    () =>
      accountWarnings?.find(
        ({ warningType, errorCode }) =>
          warningType === WarningType.Billing &&
          errorCode === WarningErrorCode.PaymentFailed,
      ),
    [accountWarnings],
  );

  if (!hasWarning) {
    return <></>;
  }

  return (
    <Container id="account-expiry-banner">
      <AnnouncementBanner
        type="error"
        closable={false}
        message={
          <Space size={96}>
            <Main>
              <FormattedMessage
                id="billing.subscription.renewal.failed.banner.message"
                values={{ b: (chunks) => <b>{chunks}</b> }}
              />
            </Main>
          </Space>
        }
      />
    </Container>
  );
};
